/**
 * Оболочка авторизованной части.
 *
 * Навигация — как в Telegram: четыре вкладки «Контакты», «Движ», «Чаты»,
 * «Настройки» и отдельная круглая кнопка поиска рядом с панелью (ПС §05,
 * изменения v1.2 и v1.3: «Знакомства» стали «Движем» — подборкой друзей по интересам). Профиль живёт внутри «Настроек» (шапка экрана S-01), центр
 * уведомлений — служебный диалог «Движ» в «Чатах», совпадения — в «Контактах».
 * §3.2: mobile/tablet — нижняя панель вкладок; desktop — левая панель;
 * wide — 240 px со значком и подписью.
 *
 * На mobile открытый диалог занимает экран целиком (как в Telegram):
 * панель вкладок скрывается, а композер встаёт к нижнему краю.
 */
import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { useUnreadConversationsCount } from '@entities/conversation/model'
import { useNewMatchesCount } from '@entities/match/model'
import { useUnreadNotificationsCount } from '@entities/notification/model'
import { usePresence } from '@shared/lib/use-presence'
import { Avatar, Badge, Icon, LogoTile, cx, type IconName } from '@shared/ui'

import { useAppStore } from '../model/app-store'
import { useDocumentBadge } from '../model/document-badge'
import { NAV_AREA_ATTR } from '../model/screen-snapshots'
import { CallBar } from '../screens/calls/CallBar'
import { PushOfferBar } from '../screens/notifications/PushPermission'
import { InstallAppHost } from '../screens/system/InstallApp'
import { RestrictionBanner } from '../screens/system/RestrictionBanner'
import styles from './AppShell.module.css'

interface NavItem {
  to: string
  label: string
  icon: IconName
  /** Заливной вариант значка — для активной вкладки. */
  iconActive: IconName
  badge?: number
}

/** Кнопка поиска рядом с панелью: глобальный поиск по контактам и никнеймам (C-28). */
const SEARCH_PATH = '/search'

/** N-07: счётчики непрочитанного на вкладках. */
function useNavItems(): NavItem[] {
  const unreadChats = useUnreadConversationsCount()
  const newMatches = useNewMatchesCount()
  const unreadNotifications = useUnreadNotificationsCount()

  // §8.6: префикс `(N)` в заголовке вкладки и значок приложения.
  useDocumentBadge(unreadChats)

  /*
   * Четыре раздела, как в Telegram. Новые совпадения считаются на «Контактах»,
   * центр уведомлений — служебный диалог «Движ», поэтому его непрочитанное
   * входит в счётчик «Чатов».
   */
  return [
    {
      to: '/contacts',
      label: 'Контакты',
      icon: 'contacts',
      iconActive: 'contacts-filled',
      badge: newMatches,
    },
    { to: '/dvizh', label: 'Движ', icon: 'dvizh', iconActive: 'dvizh-filled' },
    {
      to: '/chats',
      label: 'Чаты',
      icon: 'chats',
      iconActive: 'chats-filled',
      badge: unreadChats + unreadNotifications,
    },
    { to: '/settings', label: 'Настройки', icon: 'settings', iconActive: 'settings-filled' },
  ]
}

function useActiveSection(): string {
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  if (pathname.startsWith('/contacts') || pathname.startsWith('/matches')) return '/contacts'
  if (pathname.startsWith('/dvizh') || pathname.startsWith('/users')) return '/dvizh'
  /*
   * Изменение v1.10: канал и группа заводятся и настраиваются из «Чатов», и лента у них —
   * обычный диалог. Без этой ветки подсветка сваливалась на «Движ» по умолчанию: пользователь
   * уходил вглубь из «Чатов», а активной оказывалась соседняя вкладка.
   */
  if (
    pathname.startsWith('/chats') ||
    pathname.startsWith('/notifications') ||
    pathname.startsWith('/channels') ||
    pathname.startsWith('/groups') ||
    pathname.startsWith('/invite') ||
    pathname.startsWith('/secret') ||
    pathname.startsWith('/+')
  ) {
    return '/chats'
  }
  if (
    pathname.startsWith('/settings') ||
    pathname.startsWith('/me') ||
    pathname.startsWith('/reports') ||
    pathname.startsWith('/appeals')
  ) {
    return '/settings'
  }
  if (pathname === SEARCH_PATH) return SEARCH_PATH
  return '/dvizh'
}

/**
 * Экран, который на mobile занимает всё: панель вкладок уезжает вниз, как в Telegram.
 *
 * Открытый диалог (C-07), но не архив (C-03): `/chats/{id}` и `/chats/{id}/search`.
 * Служебный диалог «Движ» (`/notifications`) тоже занимает экран целиком.
 *
 * Изменение v1.10: сюда же экраны канала и группы (C-30…C-35) и экран приглашения — и по самой
 * ссылке `/+<код>`, и по маршруту `/invite/<код>`, куда её переводит `HandleScreen`.
 * Они открываются поверх ленты, которая панель уже спрятала, — без этого панель возвращалась
 * на полпути вглубь и съедала 98 px высоты под собой на телефоне.
 */
function useChatOpen(): boolean {
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  // Изменение v1.13: выбор собеседника для секретного чата (C-38) — тоже экран поверх «Чатов».
  return /^\/chats\/(?!archive(?:\/|$))[^/]+|^\/(?:notifications|channels|groups|invite|secret)(?:\/|$)|^\/\+/.test(
    pathname,
  )
}

export function AppShell({ children }: { children?: ReactNode }): React.ReactElement {
  const items = useNavItems()
  const active = useActiveSection()
  const chatOpen = useChatOpen()
  // Панель вкладок уезжает вниз вместе с открытием диалога и возвращается при выходе из него.
  const tabbar = usePresence(!chatOpen)

  return (
    <div className={styles['shell']}>
      <DesktopRail items={items} active={active} />

      {/* Область перехода: снимок для push/pop берётся с колонки целиком, вместе с полосами над экраном */}
      <div className={cx(styles['main'], chatOpen && styles['mainNoTabbar'])} {...{ [NAV_AREA_ATTR]: true }}>
        {/* C-44 (изменение v1.14): свёрнутый звонок — полосой над экраном, как в Telegram */}
        <CallBar />

        {/* SYS-16: активная санкция видна на любом экране, а не только в S-25 */}
        <div className={styles['restriction']}>
          <RestrictionBanner />
        </div>

        {/* PWA-01 / PWA-07: на mobile баннер стоит в потоке над экраном и не перекрывает содержимое */}
        <InstallAppHost />

        {/* N-03 полосой: над списками, но не поверх открытого чата */}
        {chatOpen ? null : <PushOfferBar />}

        <div className={styles['mainInner']}>{children ?? <Outlet />}</div>
      </div>

      {tabbar.mounted ? (
        <MobileTabBar
          items={items}
          active={active}
          closing={tabbar.closing}
          onAnimationEnd={tabbar.onAnimationEnd}
        />
      ) : null}
    </div>
  )
}

/* ===================================================================== */
/* Левая панель (desktop / wide)                                          */
/* ===================================================================== */

function DesktopRail({ items, active }: { items: NavItem[]; active: string }) {
  const profile = useAppStore((s) => s.me?.profile ?? null)
  const searchActive = active === SEARCH_PATH

  return (
    <nav className={styles['rail']} aria-label="Основная навигация">
      <div className={styles['brand']}>
        <LogoTile size={36} />
        <span className={styles['brandName']}>Движ</span>
      </div>

      <Link
        to={SEARCH_PATH}
        className={cx(styles['railItem'], searchActive && styles['railItemActive'])}
        aria-current={searchActive ? 'page' : undefined}
        aria-label="Поиск"
      >
        <span className={styles['railIcon']}>
          <Icon name="search" size={24} />
        </span>
        <span className={styles['railLabel']}>Поиск</span>
      </Link>

      {items.map((item) => {
        const isActive = active === item.to
        return (
          <Link
            key={item.to}
            to={item.to}
            className={cx(styles['railItem'], isActive && styles['railItemActive'])}
            aria-current={isActive ? 'page' : undefined}
            aria-label={item.label}
          >
            <span className={styles['railIcon']}>
              <Icon name={isActive ? item.iconActive : item.icon} size={24} />
            </span>
            <span className={styles['railLabel']}>{item.label}</span>

            {item.badge ? (
              <span className={styles['railBadge']}>
                <Badge count={item.badge} label={`${item.label}: ${item.badge} новых`} />
              </span>
            ) : null}
          </Link>
        )
      })}

      <div className={styles['railSpacer']} />

      {/* Свой профиль — внизу рельса аватаром, как в настольных мессенджерах */}
      <Link to="/me" className={cx(styles['railItem'], styles['railProfile'])} aria-label="Мой профиль">
        <span className={styles['railIcon']}>
          <Avatar
            name={profile?.display_name}
            photo={profile?.photos[0] ?? null}
            size="sm"
            className={styles['railAvatar']}
          />
        </span>
        <span className={styles['railLabel']}>Профиль</span>
      </Link>
    </nav>
  )
}

/* ===================================================================== */
/* Нижняя панель (mobile / tablet)                                        */
/* ===================================================================== */

function MobileTabBar({
  items,
  active,
  closing,
  onAnimationEnd,
}: {
  items: NavItem[]
  active: string
  closing: boolean
  onAnimationEnd: () => void
}) {
  const searchActive = active === SEARCH_PATH

  return (
    <div
      className={cx(styles['dock'], closing && styles['dockClosing'])}
      aria-hidden={closing || undefined}
      onAnimationEnd={closing ? onAnimationEnd : undefined}
    >
      <nav className={styles['tabbar']} aria-label="Основная навигация">
        {items.map((item) => {
          const isActive = active === item.to
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cx(styles['tab'], isActive && styles['tabActive'])}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className={styles['tabIcon']}>
                <Icon name={isActive ? item.iconActive : item.icon} size={24} />
                {item.badge ? (
                  <span className={styles['tabBadge']}>
                    <Badge count={item.badge} label={`${item.label}: ${item.badge} новых`} />
                  </span>
                ) : null}
              </span>
              <span className={styles['tabLabel']}>{item.label}</span>
            </Link>
          )
        })}
      </nav>

      {/* Отдельная круглая кнопка поиска рядом с панелью — как в Telegram */}
      <Link
        to={SEARCH_PATH}
        className={cx(styles['searchButton'], searchActive && styles['searchButtonActive'])}
        aria-label="Поиск"
        aria-current={searchActive ? 'page' : undefined}
      >
        <Icon name="search" size={24} />
      </Link>
    </div>
  )
}
